import { apiClient } from './apiClient';
import type { EventCategory } from '../../constants/eventCategories';

// --- Types ---

export type EventType = 'outage' | 'maintenance' | 'inspection' | 'incident' | 'weather' | 'warranty' | 'pest' | 'vegetation';
export type EventStatus = 'open' | 'acknowledged' | 'in_progress' | 'resolved' | 'closed';
export type SeverityLevel = 'low' | 'medium' | 'high' | 'critical';

export interface EventMeta {
  source_page?: number;
  source_excerpt?: string;
  theme_suggestion?: string;
  is_actionable?: boolean;
  [key: string]: unknown;
}

export interface SiteEvent {
  id: string;
  tenant_id: string;
  site_id: string;
  document_id: string | null;
  event_uid: string;
  event_type: EventType;
  title: string;
  description: string | null;
  occurred_at: string;
  resolved_at: string | null;
  severity: SeverityLevel;
  status: EventStatus;
  theme_id: string | null;
  theme_confidence: number | null;
  event_category?: EventCategory | null;
  energy_impact_kwh: number | null;
  capacity_affected_kw: number | null;
  extraction_confidence: number | null;
  manually_reviewed: boolean;
  is_safety_issue?: boolean;
  safety_resolved?: boolean;
  meta: EventMeta | null;
  created_at: string;
  updated_at: string;
}

export interface AffectedEntity {
  id: string;
  entity_type: string;
  entity_identifier: string;
  role: string;
}

export interface EventTheme {
  id: string;
  name: string;
  category: string;
}

export interface SourceLocation {
  page_number: number;
  bbox: number[];
  excerpt: string;
}

export interface SiteEventDetail extends SiteEvent {
  affected_entities: AffectedEntity[];
  theme: EventTheme | null;
}

export interface EventsListResponse {
  items: SiteEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface EventsListFilters {
  siteId: string;
  status?: EventStatus;
  severity?: SeverityLevel;
  event_type?: EventType;
  theme_id?: string;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// --- Intelligence summary types ---

export interface EventSummary {
  id: string;
  title: string;
  event_type: string;
  severity: string;
  status: string;
  occurred_at: string;
  resolved_at: string | null;
  document_id: string | null;
  document_filename: string | null;
  event_category?: EventCategory | null;
  affected_entities: Array<{ entity_type: string; entity_identifier: string; role: string; asset_id?: string | null }>;
}

export interface TaskSummary {
  id: string;
  title: string;
  task_type: string;
  status: string;
  priority: number;
  due_date: string | null;
  source_event_id: string | null;
  auto_created: boolean;
}

export interface TaskGroupSummary {
  id: string;
  name: string;
  group_type: string;
  status: string;
  task_count: number;
  event_count: number;
}

export interface SiteIntelligenceResponse {
  site_id: string;
  site_name: string;
  events_total: number;
  events: EventSummary[];
  tasks_total: number;
  tasks: TaskSummary[];
  task_groups: TaskGroupSummary[];
}

// --- API ---

export const eventsApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    listSiteEvents: builder.query<EventsListResponse, EventsListFilters>({
      query: ({ siteId, ...filters }) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
        const qs = params.toString();
        return {
          url: `/api/v1/sites/${siteId}/events${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, _error, { siteId }) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'Event' as const, id })),
              { type: 'Event' as const, id: `LIST-${siteId}` },
            ]
          : [{ type: 'Event' as const, id: `LIST-${siteId}` }],
    }),

    getSiteEvent: builder.query<SiteEventDetail, { siteId: string; eventId: string }>({
      query: ({ siteId, eventId }) => ({
        url: `/api/v1/sites/${siteId}/events/${eventId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { eventId }) => [{ type: 'Event' as const, id: eventId }],
    }),

    getDocumentEvents: builder.query<SiteEvent[], { documentId: string }>({
      query: ({ documentId }) => ({
        url: `/api/v1/documents/${documentId}/events`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? result.map(({ id }) => ({ type: 'Event' as const, id }))
          : [],
    }),

    getSiteIntelligence: builder.query<SiteIntelligenceResponse, { siteId: string; eventsLimit?: number; tasksLimit?: number }>({
      query: ({ siteId, eventsLimit = 100, tasksLimit = 100 }) => ({
        url: `/api/v1/intelligence/sites/${siteId}?events_limit=${eventsLimit}&tasks_limit=${tasksLimit}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { siteId }) => [
        { type: 'Event' as const, id: `INTEL-${siteId}` },
        { type: 'Task' as const, id: `INTEL-${siteId}` },
      ],
    }),

  }),
  overrideExisting: true,
});

export const {
  useListSiteEventsQuery,
  useLazyListSiteEventsQuery,
  useGetSiteEventQuery,
  useGetDocumentEventsQuery,
  useGetSiteIntelligenceQuery,
} = eventsApi;
